import React, { useEffect, useRef, useState } from "react";
import { RecIcon } from "../materials/MaterialRecordParts";
import {
  formatLogDay,
  formatLogDuration,
  formatLogRange,
  payrollPill,
} from "./timeLogModel";
import "../materials/materialRecord.css";
import "./timeLogs.css";

/**
 * One Time Log. Reusable anywhere a chronological activity record is listed
 * (Time Logs hub today; Project → Labour / employee history later): pass the
 * log, decide which optional lines to show, and supply menu items.
 *
 *   showEmployee  – employee line (hidden when the list is already one person)
 *   showDate      – date line (hidden under a date heading)
 *   showProject   – project/client lines (hidden when filtered to one project)
 *   menuItems     – [{ key, label, onSelect, danger }] for the ⋮ menu
 *   onOpen        – omit for a read-only row (e.g. Project → Labour without time log access)
 *   amount        – optional money label on the right (labour cost)
 *   detail        – optional extra line (e.g. "$75.00/hr")
 *   showStatus    – Paid/Unpaid pill (default on; off when `amount` is shown)
 */
export function TimeLogRow({
  log,
  onOpen,
  showEmployee = false,
  showDate = false,
  showProject = true,
  menuItems = [],
  amount = "",
  detail = "",
  showStatus = true,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (event) => {
      if (event.type === "keydown") {
        if (event.key === "Escape") setMenuOpen(false);
        return;
      }
      if (!menuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [menuOpen]);

  const pill = payrollPill(log);
  const range = formatLogRange(log);
  const workType = String(log.work_category || "").trim();
  const projectName = log.project_name || "No Project";
  const label = `${formatLogDuration(log)}, ${projectName}`;

  const content = (
    <>
      <span className="tl-row__time">
          <span className="tl-row__dur">{formatLogDuration(log)}</span>
          {range ? <span className="tl-row__range">{range}</span> : null}
          {showDate ? <span className="tl-row__range">{formatLogDay(log.date_iso, { long: false })}</span> : null}
        </span>
        <span className="tl-row__body">
          {showProject ? <span className="tl-row__project">{projectName}</span> : null}
          {showProject && log.client_name ? (
            <span className="tl-row__sub">{log.client_name}</span>
          ) : null}
          {showEmployee && log.employee ? (
            <span className="tl-row__sub tl-row__sub--emp">
              <RecIcon name="user" />
              {log.employee}
            </span>
          ) : null}
          {detail ? <span className="tl-row__sub">{detail}</span> : null}
          {workType ? (
            <span className="tl-row__sub tl-row__sub--type">
              <RecIcon name="wrench" />
              {workType}
            </span>
          ) : null}
        </span>
    </>
  );

  return (
    <div className="tl-row" data-testid="time-log-row">
      {onOpen ? (
        <button
          type="button"
          className="tl-row__main"
          onClick={() => onOpen(log)}
          aria-label={`Open time log: ${label}`}
        >
          {content}
        </button>
      ) : (
        <div className="tl-row__main tl-row__main--static">{content}</div>
      )}

      <div className="tl-row__side">
        {menuItems.length ? (
          <div className="tl-row__menuwrap" ref={menuRef}>
            <button
              type="button"
              className="tl-row__more"
              aria-label="Time log actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <RecIcon name="more" className="tl-row__more-icon" />
            </button>
            {menuOpen ? (
              <div className="mat-menu tl-row__menu" role="menu">
                {menuItems.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    role="menuitem"
                    className={`mat-menu__item${item.danger ? " tl-menu-danger" : ""}`}
                    onClick={() => {
                      setMenuOpen(false);
                      item.onSelect(log);
                    }}
                  >
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        {amount ? <span className="tl-row__amount">{amount}</span> : null}
        {showStatus ? (
          <span className={`rec-pill tl-pill tl-pill--${pill.tone}`}>{pill.label}</span>
        ) : null}
      </div>
    </div>
  );
}

/** Date heading with the day's total, e.g. "TUE, OCT 6, 2026   19h 22m · 1 log". */
export function TimeLogDayHeader({ label, durationLabel, countLabel, extra = "" }) {
  return (
    <div className="tl-day" role="heading" aria-level={2}>
      <span className="tl-day__label">{label}</span>
      <span className="tl-day__total">
        {durationLabel} · {countLabel}
        {extra ? ` · ${extra}` : ""}
      </span>
    </div>
  );
}
